<!-- RecipeDetail.svelte -->
<script>
  import { createEventDispatcher } from 'svelte';
  import { getIngredientMatchPercentage } from '../store.js';
  import { selectedIngredients } from '../store.js';
  
  export let recipe;
  
  const dispatch = createEventDispatcher();
  
  function closeModal() {
    dispatch('close');
  }
  
  // Calculate ingredient match percentage
  $: ingredientMatchPercentage = getIngredientMatchPercentage(recipe, $selectedIngredients);
  
  // Check if ingredients are in the selected list
  function isIngredientAvailable(ingredient) {
    return $selectedIngredients.some(selected => 
      selected.name.toLowerCase().includes(ingredient.name.toLowerCase()) ||
      ingredient.name.toLowerCase().includes(selected.name.toLowerCase())
    );
  }
</script>

<div class="modal-backdrop" on:click={closeModal}>
  <div class="modal-content" on:click|stopPropagation>
    <button class="close-button" on:click={closeModal}>×</button>
    
    <div class="recipe-header">
      <div class="recipe-image-placeholder">
        {recipe.name.charAt(0)}
      </div>
      <div class="recipe-title">
        <h2>{recipe.name}</h2>
        <div class="recipe-match">
          <div class="match-progress">
            <div class="match-bar" style="width: {ingredientMatchPercentage}%"></div>
          </div>
          <span class="match-text">{ingredientMatchPercentage}% matchande ingredienser</span>
        </div>
      </div>
    </div>
    
    <div class="recipe-details">
      <div class="detail-row">
        <div class="detail-item">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          <span>{recipe.prepTime} min</span>
        </div>
        
        <div class="detail-item">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
          <span>{recipe.difficulty}</span>
        </div>
        
        <div class="detail-item">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          <span>{recipe.portions} portioner</span>
        </div>
        
        <div class="detail-item">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
          </svg>
          <span>{recipe.category}</span>
        </div>
      </div>
    </div>
    
    <div class="recipe-sections">
      <div class="ingredients-section">
        <h3>Ingredienser</h3>
        <ul class="ingredients-list">
          {#each recipe.ingredients as ingredient}
            <li class:available={isIngredientAvailable(ingredient)}>
              <span class="ingredient-amount">{ingredient.amount}</span>
              <span class="ingredient-name">{ingredient.name}</span>
              {#if isIngredientAvailable(ingredient)}
                <span class="availability-badge">På extrapris!</span>
              {/if}
            </li>
          {/each}
        </ul>
      </div>
      
      <div class="instructions-section">
        <h3>Tillagning</h3>
        <p>{recipe.instructions}</p>
      </div>
    </div>
    
    {#if recipe.nutritionInfo}
      <div class="nutrition-section">
        <h3>Näringsvärde (per portion)</h3>
        <div class="nutrition-grid">
          <div class="nutrition-item">
            <span class="nutrition-value">{recipe.nutritionInfo.calories}</span>
            <span class="nutrition-label">kcal</span>
          </div>
          <div class="nutrition-item">
            <span class="nutrition-value">{recipe.nutritionInfo.protein}g</span>
            <span class="nutrition-label">protein</span>
          </div>
          <div class="nutrition-item">
            <span class="nutrition-value">{recipe.nutritionInfo.carbs}g</span>
            <span class="nutrition-label">kolhydrater</span>
          </div>
          <div class="nutrition-item">
            <span class="nutrition-value">{recipe.nutritionInfo.fat}g</span>
            <span class="nutrition-label">fett</span>
          </div>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 20px;
  }
  
  .modal-content {
    background-color: white;
    border-radius: 10px;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
    width: 100%;
    max-width: 800px;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
  }
  
  .close-button {
    position: absolute;
    top: 10px;
    right: 15px;
    background: none;
    border: none;
    font-size: 30px;
    cursor: pointer;
    color: #666;
    z-index: 10;
  }
  
  .recipe-header {
    display: flex;
    align-items: center;
    padding: 30px;
    background-color: #f8f8f8;
    border-bottom: 1px solid #eee;
    border-radius: 10px 10px 0 0;
  }
  
  .recipe-image-placeholder {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: #e91e63;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 32px;
    margin-right: 20px;
    flex-shrink: 0;
  }
  
  .recipe-title {
    flex: 1;
  }
  
  .recipe-title h2 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 24px;
    border: none;
    padding: 0;
  }
  
  .recipe-match {
    display: flex;
    flex-direction: column;
  }
  
  .match-progress {
    width: 100%;
    height: 8px;
    background-color: #eee;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 5px;
  }
  
  .match-bar {
    height: 100%;
    background-color: #4caf50;
    border-radius: 10px;
  }
  
  .match-text {
    font-size: 14px;
    color: #666;
  }
  
  .recipe-details {
    padding: 20px 30px;
    border-bottom: 1px solid #eee;
  }
  
  .detail-row {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }
  
  .detail-item {
    display: flex;
    align-items: center;
    color: #666;
  }
  
  .detail-item svg {
    margin-right: 8px;
  }
  
  .recipe-sections {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 20px;
    padding: 30px;
  }
  
  .ingredients-section, .instructions-section {
    margin-bottom: 20px;
  }
  
  h3 {
    color: #333;
    margin: 0 0 15px 0;
    font-size: 18px;
  }
  
  .ingredients-list {
    list-style-type: none;
    padding: 0;
    margin: 0;
  }
  
  .ingredients-list li {
    padding: 8px 10px;
    margin-bottom: 8px;
    border-radius: 5px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    background-color: #f8f8f8;
  }
  
  .ingredients-list li.available {
    background-color: #e8f5e9;
    border-left: 3px solid #4caf50;
  }
  
  .ingredient-amount {
    font-weight: bold;
    margin-right: 10px;
  }
  
  .availability-badge {
    margin-left: auto;
    background-color: #4caf50;
    color: white;
    font-size: 12px;
    padding: 3px 6px;
    border-radius: 3px;
  }
  
  .instructions-section p {
    line-height: 1.6;
    color: #333;
  }
  
  .nutrition-section {
    padding: 0 30px 30px 30px;
  }
  
  .nutrition-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 15px;
    text-align: center;
  }
  
  .nutrition-item {
    padding: 15px;
    background-color: #f8f8f8;
    border-radius: 8px;
  }
  
  .nutrition-value {
    display: block;
    font-size: 20px;
    font-weight: bold;
    color: #333;
    margin-bottom: 5px;
  }
  
  .nutrition-label {
    display: block;
    font-size: 12px;
    color: #666;
  }
  
  @media (max-width: 768px) {
    .recipe-sections {
      grid-template-columns: 1fr;
    }
    
    .nutrition-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
</style>